import { memo, useState } from 'react';

import { AlertTriangle, Bot, Check, Copy, Square, User } from 'lucide-react';

import type { ChatMessage } from '../types';
import { cn } from '../utils/cn';
import { FormattedAnswer } from './FormattedAnswer';

const TypingDots = () => (
    <div className="flex items-center gap-1 py-1" aria-label="Assistant is thinking">
        {[0, 150, 300].map((delay) => (
            <span
                key={delay}
                className="h-2 w-2 rounded-full bg-emerald-500/70 animate-bounce"
                style={{ animationDelay: `${delay}ms` }}
            />
        ))}
    </div>
);

const formatTime = (ts: number) =>
    new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export const ChatMessageBubble = memo(({ message }: { message: ChatMessage }) => {
    const [copied, setCopied] = useState(false);
    const isUser = message.role === 'user';
    const isStreaming = message.status === 'streaming';
    const isWaiting = isStreaming && message.content === '';

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(message.content);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            /* clipboard unavailable */
        }
    };

    return (
        <div className={cn('group flex gap-3', isUser ? 'flex-row-reverse' : 'flex-row')}>
            <div
                className={cn(
                    'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg shadow-sm',
                    isUser
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white',
                )}
            >
                {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
            </div>

            <div className={cn('flex max-w-[85%] flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
                <div
                    className={cn(
                        'rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm break-words',
                        isUser
                            ? 'bg-indigo-600 text-white rounded-tr-sm whitespace-pre-wrap'
                            : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-800 rounded-tl-sm',
                        message.status === 'error' &&
                            'border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300',
                    )}
                >
                    {isWaiting ? (
                        <TypingDots />
                    ) : isUser ? (
                        message.content
                    ) : (
                        <>
                            <FormattedAnswer text={message.content} />
                            {isStreaming && (
                                <span className="ml-0.5 inline-block h-4 w-1.5 translate-y-0.5 animate-pulse rounded-sm bg-emerald-500" />
                            )}
                        </>
                    )}
                </div>

                <div
                    className={cn(
                        'flex items-center gap-2 px-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400',
                        isUser ? 'flex-row-reverse' : 'flex-row',
                    )}
                >
                    <span>{formatTime(message.createdAt)}</span>
                    {message.status === 'stopped' && (
                        <span className="inline-flex items-center gap-1 text-amber-500">
                            <Square className="h-2.5 w-2.5" /> Stopped
                        </span>
                    )}
                    {message.status === 'error' && (
                        <span className="inline-flex items-center gap-1 text-rose-500">
                            <AlertTriangle className="h-3 w-3" /> Error
                        </span>
                    )}
                    {!isUser && !isStreaming && message.content && message.id !== 'welcome' && (
                        <button
                            type="button"
                            onClick={handleCopy}
                            className="inline-flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 hover:text-slate-600 dark:hover:text-slate-200"
                            aria-label="Copy answer"
                        >
                            {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
                            {copied ? 'Copied' : 'Copy'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
});

ChatMessageBubble.displayName = 'ChatMessageBubble';
